import { type LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

export interface MetricItem {
  label: string;
  value: number;
  icon: LucideIcon;
  color?: string;
  /** Shown beside the primary number (e.g. "retryable"). */
  valueCaption?: string;
  /** Second statistic in the same card (e.g. locked failures). */
  secondary?: { value: number; label: string };
  /** When set, the card links to this route (e.g. webhook failures queue). */
  href?: string;
}

interface MetricCardsProps {
  metrics: MetricItem[];
  loading?: boolean;
  /**
   * Render a tight, low-height stat strip instead of full cards. Useful on
   * dense pages (e.g. daily intake) where the KPI row should not dominate the
   * viewport above the working table.
   */
  compact?: boolean;
}

export function MetricCards({ metrics, loading, compact }: MetricCardsProps) {
  if (compact) {
    return <CompactMetricCards metrics={metrics} loading={loading} />;
  }
  const n = Math.max(metrics.length, 1);
  const colsClass =
    n >= 6
      ? "md:grid-cols-6"
      : n >= 5
        ? "md:grid-cols-5"
        : n === 4
          ? "md:grid-cols-4"
          : n === 3
            ? "md:grid-cols-3"
            : "md:grid-cols-2";
  if (loading) {
    return (
      <div className={`grid gap-4 ${colsClass}`}>
        {Array.from({ length: Math.max(metrics.length, 6) }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-4" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-10 w-24" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className={`grid gap-4 ${colsClass}`}>
      {metrics.map((m) => {
        const card = (
          <Card
            className={
              m.href
                ? "transition-colors hover:border-primary/40 hover:bg-muted/30"
                : undefined
            }
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {m.label}
              </CardTitle>
              <m.icon className={`h-4 w-4 ${m.color || "text-muted-foreground"}`} />
            </CardHeader>
            <CardContent className="space-y-1.5">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
                <span className="text-2xl font-bold tabular-nums">
                  {m.value.toLocaleString()}
                </span>
                {m.valueCaption ? (
                  <span className="text-xs font-medium text-muted-foreground">
                    {m.valueCaption}
                  </span>
                ) : null}
              </div>
              {m.secondary ? (
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
                  <span className="text-xl font-semibold tabular-nums text-muted-foreground">
                    {m.secondary.value.toLocaleString()}
                  </span>
                  <span className="text-xs text-muted-foreground">{m.secondary.label}</span>
                </div>
              ) : null}
            </CardContent>
          </Card>
        );
        return m.href ? (
          <Link key={m.label} to={m.href} className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {card}
          </Link>
        ) : (
          card
        );
      })}
    </div>
  );
}

function CompactMetricCards({ metrics, loading }: MetricCardsProps) {
  if (loading) {
    return (
      <div className="flex flex-wrap gap-2">
        {Array.from({ length: Math.max(metrics.length, 6) }).map((_, i) => (
          <Skeleton key={i} className="h-[46px] w-[120px] rounded-md" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {metrics.map((m) => {
        const inner = (
          <>
            <m.icon
              className={`h-4 w-4 shrink-0 ${m.color || "text-muted-foreground"}`}
            />
            <div className="min-w-0 leading-tight">
              <div className="text-[11px] font-medium text-muted-foreground truncate">
                {m.label}
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-base font-bold tabular-nums">
                  {m.value.toLocaleString()}
                </span>
                {m.valueCaption ? (
                  <span className="text-[10px] font-medium text-muted-foreground">
                    {m.valueCaption}
                  </span>
                ) : null}
                {m.secondary ? (
                  <span className="text-[10px] text-muted-foreground">
                    · {m.secondary.value.toLocaleString()} {m.secondary.label}
                  </span>
                ) : null}
              </div>
            </div>
          </>
        );
        const base =
          "flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5";
        return m.href ? (
          <Link
            key={m.label}
            to={m.href}
            className={`${base} transition-colors hover:border-primary/40 hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
          >
            {inner}
          </Link>
        ) : (
          <div key={m.label} className={base}>
            {inner}
          </div>
        );
      })}
    </div>
  );
}
